import { combineReducers, AnyAction } from 'redux';

import type { ThunkDispatch } from 'redux-thunk';

import type { KeyedReturnTypes } from '@gumtree/shared/src/model/generic.model';
import commonReducers, { updateCommonData } from '@gumtree/shell/src/reducers/common';
import buyerReducers, { updateBuyerData } from '@gumtree/shell/src/reducers/buyer';
import page, { updatePage } from '@gumtree/shell/src/reducers/page';
import fullscreenFilter from '@gumtree/shared/src/reducers/fullscreen-filter';
import { SimplifiedSuggestion } from '@gumtree/ui-library/src/utils/suggestions-service';

import type { Transformed } from '../get-data/transform';
import searchAds, { updateSearchAds } from './update-search-ads';
import breadcrumbs, { updateBreadcrumbs } from './breadcrumbs';
import adsTitle, { updateAdsTitle } from './update-ads-title';
import locationTitle, { updateLocationTitle } from './location-title';
import adsPagination, { updateAdsPagination } from './update-ads-pagination';
import nearbyAds, { updateNearbyAds } from './update-nearby-ads';
import saveSearch, { setSavedSearches } from './save-search';
import distanceFilter, { updateDistanceFilter } from './distance-filter';
import locationList from './location-list';
import filtersTagsList, { updateFiltersTagsList } from './update-filters-tags-list';
import currentFilterStates, { setFiltersBulk } from './current-filter-states';
import searchResultAbundance, { updateSearchResultAbundance } from './search-result-abundance';
import seoFaq, { updateSeoFaq } from './seo-faq';
import seoProduct, { updateSeoPro } from './seo-product';
import noResultsMessage, { updateNoResultsMessage } from './update-no-results-message';
import isDominantCategoryActive, {
    updateIsDominantCategoryActive,
} from './update-is-dominant-category-active';
import adBlockers, { updateAdBlockers } from './update-ad-blockers';
import linkRecommenderList, { updateLinkRecommenderList } from './update-link-recommender-list';
import alternativeKeywordSuggestions, {
    updateAlternativeSearchSuggestions,
} from './update-alternative-search-suggestions';
import keywordCorrection, { updateKeywordSuggestion } from './keyword-correction';
import query, { updateQuery } from './query';
import filtersDictionary, { updateFiltersDictionary } from './filters-dictionary';
import filtersOrder, { updateFiltersOrder } from './filters-order';
import categoriesFilter, { updateCategoriesFilter } from './categories-filter';
import sortFilter, { updateSortFilter } from './sort-filter';
import showDeliveryTile, { updateShowDeliveryTile } from './delivery-tile';
import postAJob, { updatePostAJob } from './post-a-job';
import { PostAJobData } from '../types';
import categories, { updateCategories } from './categories';
import multiFiltersOrder, { updateMultiFiltersOrder } from './multi-filters-order';

export const showPage =
    ({
        title,
        description,
        breadcrumbs,
        saveSearch,
        adBlockers,
        resultsPage,
        alternativeKeywordSuggestions,
        correctedKeywordForUndo,
        correctedKeywordForRedo,
        postAJob,
        ...data
    }: Transformed & {
        alternativeKeywordSuggestions: SimplifiedSuggestion[];
        correctedKeywordForUndo: undefined | string;
        correctedKeywordForRedo: undefined | string;
        postAJob: PostAJobData;
    }) =>
    (dispatch: SrpUseDispatch) => {
        dispatch(updateCommonData(data));
        dispatch(updateBuyerData(data));
        dispatch(updatePage({ title, description }));
        dispatch(updateSearchAds(resultsPage.searchAds));
        dispatch(updateBreadcrumbs(breadcrumbs));
        dispatch(updateAdsTitle(resultsPage.adsTitle));
        dispatch(updateLocationTitle(resultsPage.locationTitle));
        dispatch(updateAdsPagination(resultsPage.adsPagination));
        dispatch(updateNearbyAds(resultsPage.nearbyAds));
        dispatch(setSavedSearches(saveSearch));
        dispatch(setFiltersBulk(resultsPage.currentFilterStates));
        dispatch(updateDistanceFilter(resultsPage.distanceFilter));
        dispatch(updateFiltersTagsList(resultsPage.filtersTagsList));
        dispatch(updateNoResultsMessage(resultsPage.noResultsMessage));
        dispatch(updateSeoFaq(resultsPage.seoFaq));
        dispatch(updateSeoPro(resultsPage.product));
        dispatch(updateIsDominantCategoryActive(resultsPage.isDominantCategoryActive));
        dispatch(updateAdBlockers(adBlockers));
        dispatch(updateLinkRecommenderList(resultsPage.linkRecommenderList));
        dispatch(updateAlternativeSearchSuggestions(alternativeKeywordSuggestions));
        dispatch(updateQuery(resultsPage.query));
        dispatch(updateFiltersDictionary(resultsPage.filtersDictionary, data.baseConfig.category));
        dispatch(updateFiltersOrder(resultsPage.filtersOrder));
        dispatch(updateMultiFiltersOrder(resultsPage.multiFiltersOrder));
        dispatch(updateCategoriesFilter(resultsPage.categoriesFilter));
        dispatch(updateSortFilter(resultsPage.sortFilter));
        dispatch(updateSearchResultAbundance(resultsPage.searchResultAbundance));
        dispatch(
            updateKeywordSuggestion({ autoCorrectInitial: resultsPage.keywordAutoCorrection })
        );
        dispatch(updateShowDeliveryTile(resultsPage.showDeliveryTile));
        dispatch(updatePostAJob(postAJob));
        dispatch(updateCategories(resultsPage.categories));
        if (correctedKeywordForUndo) {
            dispatch(
                updateKeywordSuggestion({
                    autoCorrectUndone: { correctedKeyword: correctedKeywordForUndo },
                })
            );
        }
        if (correctedKeywordForRedo) {
            dispatch(
                updateKeywordSuggestion({
                    autoCorrectRedone: { correctedKeyword: correctedKeywordForRedo },
                })
            );
        }
    };

const index = {
    ...commonReducers,
    ...buyerReducers,
    resultsPage: combineReducers({
        query,
        adsTitle,
        nearbyAds,
        fullscreenFilter,
        locationTitle,
        searchResultAbundance,
        noResultsMessage,
        seoFaq,
        product: seoProduct,
        alternativeKeywordSuggestions,
        linkRecommenderList,
        isDominantCategoryActive,
        filtersTagsList,
        distanceFilter,
        searchAds,
        keywordCorrection,
        currentFilterStates,
        filtersDictionary,
        filtersOrder,
        multiFiltersOrder,
        categoriesFilter,
        sortFilter,
        adsPagination,
        showDeliveryTile,
        categories,
    }),
    page,
    breadcrumbs,
    saveSearch,
    locationList,
    adBlockers,
    postAJob,
};

export default index;

export type SrpState = KeyedReturnTypes<typeof index>;

export type SrpUseDispatch = ThunkDispatch<SrpState, any, AnyAction>;
